import type { Transition } from 'motion/react';

/**
 * Las animaciones de toda la app, en un solo lugar: si cada pantalla elige su
 * duración y su curva, el sistema se siente armado con retazos.
 *
 * Se usan esparcidas sobre un `motion.*`:
 *
 *     <motion.div {...aparecer}>…</motion.div>
 *     {filas.map((f, i) => <motion.tr key={f.id} {...fila(i)}>…</motion.tr>)}
 *     <AnimatePresence>{abierto && <motion.div {...desplegar}>…</motion.div>}</AnimatePresence>
 *
 * Cortas a propósito: es una herramienta de trabajo, no una vidriera. Nada
 * debería tardar más de un cuarto de segundo en quedar quieto.
 *
 * Quien tiene activado «reducir movimiento» en el sistema no ve los
 * desplazamientos: lo resuelve `MotionConfig reducedMotion="user"` en main.tsx.
 */

/**
 * La curva de todo: arranca con decisión y se apoya muy despacio al final.
 * Es esa cola larga lo que hace que algo se sienta suave y no que «aparece».
 */
export const CURVA = [0.16, 1, 0.3, 1] as const;

export const suave: Transition = { duration: 0.34, ease: CURVA };

/** El cajón del menú y las bandejas que entran por un costado: la curva de un cajón que se desliza. */
export const deslizar: Transition = { duration: 0.42, ease: [0.32, 0.72, 0, 1] };

/** Para lo que se arrastra o cambia de lugar (`layout`): un resorte sin rebote. */
export const resorte: Transition = { type: 'spring', stiffness: 300, damping: 34 };

/** Un bloque que entra: una tarjeta, un aviso, el contenido de una pantalla. */
export const aparecer = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6, transition: { duration: 0.2, ease: CURVA } },
  transition: suave,
};

/**
 * Una fila de una lista, escalonada. El escalón se corta en la duodécima: en
 * una lista de 25 la última no puede llegar medio segundo tarde.
 */
export const fila = (i: number) => ({
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, x: -16, transition: { duration: 0.22, ease: CURVA } },
  transition: { ...suave, delay: Math.min(i, 12) * 0.035 },
});

/** Lo que flota encima: un menú, un panel, un diálogo. Crece desde donde está. */
export const pop = {
  initial: { opacity: 0, scale: 0.94, y: -4 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.96, y: -4, transition: { duration: 0.18, ease: CURVA } },
  transition: { duration: 0.28, ease: CURVA },
};

/** Lo que se abre o se cierra en el lugar (un detalle, un formulario). */
export const desplegar = {
  initial: { opacity: 0, height: 0 },
  // La altura se acomoda con la curva larga y el contenido aparece un poco
  // después: si los dos van juntos, el texto se ve aplastado mientras crece.
  animate: { opacity: 1, height: 'auto', transition: { height: suave, opacity: { duration: 0.25, delay: 0.08 } } },
  exit: { opacity: 0, height: 0, transition: { height: { duration: 0.26, ease: CURVA }, opacity: { duration: 0.12 } } },
  transition: suave,
  style: { overflow: 'hidden' },
};

/**
 * Se abre desde el centro hacia los dos costados, como una cortina que se
 * separa en dos: un diálogo, un campo que se despliega, una tarjeta nueva.
 * `radio` es el redondeo del elemento, para que el recorte no lo cuadre.
 */
export const abrir = (radio = 16) => ({
  initial: { opacity: 0, clipPath: `inset(0% 50% 0% 50% round ${radio}px)` },
  animate: { opacity: 1, clipPath: `inset(0% 0% 0% 0% round ${radio}px)` },
  exit: {
    opacity: 0,
    clipPath: `inset(0% 50% 0% 50% round ${radio}px)`,
    transition: { duration: 0.24, ease: CURVA },
  },
  transition: { clipPath: { duration: 0.46, ease: CURVA }, opacity: { duration: 0.2 } },
});

/**
 * Crece en círculo desde un punto: lo que se abre desde un botón (la campana,
 * la cámara) nace en el botón y se expande hasta su lugar. `x` e `y` son
 * posiciones CSS relativas al elemento que se revela (p. ej. `calc(100% - 19px)`, `-27px`).
 */
export const revelarDesde = (x: string, y: string) => ({
  initial: { opacity: 0, clipPath: `circle(0px at ${x} ${y})` },
  animate: { opacity: 1, clipPath: `circle(150% at ${x} ${y})` },
  exit: { opacity: 0, clipPath: `circle(0px at ${x} ${y})`, transition: { duration: 0.28, ease: CURVA } },
  transition: { clipPath: { duration: 0.55, ease: CURVA }, opacity: { duration: 0.18 } },
});

/** Lo que sube desde abajo en el celular (una bandeja, el resultado del escaneo). */
export const subir = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 40, transition: { duration: 0.24, ease: CURVA } },
  transition: deslizar,
};
